// Client-side creations: single catalogue (official + local drafts + the collection's published creations), local
// drafts, publishing, store search and AI workshop. What the player added is data/collection.ts (ADR 0017).
import * as C from '@gruvetto/contracts';
import { Catalog, officialEntries, validateCreation, toWorkout, toTrackDef, toCustomItem, worldOf, OFFICIAL_WORLDS, planTotal, planSlot, adaptPlan, officialCosmeticOf, OFFICIAL_THEMES, type Entry, type AnyEntry, type Validation } from '@gruvetto/creations';
import { withoutItems, fitnessSeries } from '@gruvetto/core';
import { listActivities } from './activities.js';
import type { TrackDef } from '@gruvetto/core';
import { OFFICIAL_GAMES } from '@gruvetto/creations/games';
import { MODES, TRACKS, isModeId, isUnlocked, levelFromXp, type Workout } from '@gruvetto/core';
import { db } from './db.js';
import { state, update } from './store.js';
import { api, currentUser, ApiError } from './api.js';

export type { Entry, AnyEntry };
export const catalog = new Catalog(() => state.profile.ftp);
catalog.put(...officialEntries(), ...OFFICIAL_GAMES);

const rid = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 7);

/** Custom workout saved before creations existed. */
interface LegacyWorkout { id: string; name: string; desc?: string; cat?: string; test?: C.WorkoutContent['test']; segs: C.WorkoutContent['segs'] }

/**
 * Loads drafts and legacy custom workouts. Library copies installed on this device before the collection moved to the
 * server (ADR 0017) are kept aside: data/collection.ts sends them to the account once, then drops them.
 */
export let legacyInstalled: AnyEntry[] = [];
export const catalogReady: Promise<void> = (async () => {
  const [stored, legacy] = await Promise.all([db.all<AnyEntry>('creations'), db.all<LegacyWorkout>('workouts')]);
  const mine = stored.filter(e => e.source === 'local');
  legacyInstalled = stored.filter(e => e.source === 'library');
  const old = legacy.filter(w => !mine.some(m => m.id === w.id)).map((w): AnyEntry => ({ id: w.id, version: 1, source: 'local', author: null, updatedAt: 1, creation: { kind: 'workout', name: w.name, desc: w.desc || '', tags: ['perso'], content: { cat: w.cat || 'endurance', ...(w.test ? { test: w.test } : {}), segs: w.segs } } }));
  catalog.put(...mine, ...old);
})().catch(e => console.warn('Local creations unavailable', e));

export const validate = (c: unknown): Validation => validateCreation(c, catalog.ctx);

// ---------- Drafts and collection ----------
export interface SaveMeta { id?: string; remixOf?: Entry['remixOf']; provenance?: C.Provenance | null; remote?: Entry['remote'] }

/** Saves (or updates) a local draft. An official or library creation becomes a local remix. */
export async function saveLocal(creation: C.CreationInput, meta: SaveMeta = {}): Promise<AnyEntry> {
  const prev = meta.id ? catalog.get(meta.id) : null, mine = prev?.source === 'local';
  const e = {
    id: mine ? prev!.id : 'loc_' + rid(), version: mine ? prev!.version + 1 : 1, source: 'local', creation,
    author: currentUser() ? { id: currentUser()!.id, name: currentUser()!.name } : null, updatedAt: Date.now(),
    remixOf: mine ? prev!.remixOf : prev ? { id: prev.remote?.id ?? prev.id, version: prev.remote?.version ?? prev.version } : meta.remixOf ?? null,
    remote: mine ? prev!.remote ?? null : meta.remote ?? null, provenance: meta.provenance ?? (mine ? prev!.provenance : null) ?? null,
  } as AnyEntry;
  await db.put('creations', e);
  catalog.put(e);
  return e;
}
export async function deleteLocal(id: string) { await db.del('creations', id); await db.del('workouts', id); catalog.remove(id); }

// ---------- Store search ----------
export const browse = (q: Partial<Record<'kind' | 'q' | 'tag' | 'sort' | 'mine' | 'content', string>> & { limit?: number; offset?: number } = {}) =>
  api<C.ListedCreation[]>('/creations?' + new URLSearchParams(Object.entries(q).filter(([, v]) => v != null && v !== '').map(([k, v]) => [k, String(v)])).toString());
export const fetchCreation = (id: string, version?: number) => api<C.Creation>(`/creations/${encodeURIComponent(id)}${version ? `/versions/${version}` : ''}`);
export const rate = (id: string, stars: number) => api(`/creations/${encodeURIComponent(id)}/rating`, { method: 'POST', body: { stars } });

/** Play of a published creation: library counter (silent offline). */
export function recordPlay(e: AnyEntry | Entry<'game'> | null, finished: boolean) {
  const id = e?.remote?.id;
  if (id) api(`/creations/${encodeURIComponent(id)}/plays`, { method: 'POST', body: { finished } }).catch(() => {});
}

/** A published program must be self-contained: the non-official workouts it cites are embedded. */
function selfContained(c: C.CreationInput): C.CreationInput {
  if (c.kind !== 'plan') return c;
  const own = new Set(c.content.workouts.map(w => w.id)), extra: C.PlanContent['workouts'] = [];
  for (const wk of c.content.weeks) for (const s of wk.sessions) {
    const e = catalog.get(s.workout, 'workout');
    if (own.has(s.workout) || !e || e.source === 'builtin') continue;
    own.add(s.workout);
    extra.push({ id: s.workout.replace(/[^a-z0-9_-]/gi, '-').slice(0, 60), name: e.creation.name, desc: e.creation.desc, ...e.creation.content });
  }
  return extra.length ? { ...c, content: { ...c.content, workouts: [...c.content.workouts, ...extra] } } : c;
}

/** Publishes a draft (new creation or new version) and keeps the link with the published copy. The store lists it from there. */
export async function publish(e: AnyEntry, { visibility = 'public', price = 0 }: { visibility?: 'public' | 'unlisted' | 'private'; price?: number } = {}): Promise<C.Creation> {
  const body = { creation: selfContained(e.creation), visibility, price, ...(e.remixOf && !e.remote ? { remixOf: e.remixOf } : {}), ...(e.provenance ? { provenance: e.provenance } : {}) };
  const r = e.remote ? await api<C.Creation>(`/creations/${e.remote.id}`, { method: 'PUT', body }) : await api<C.Creation>('/creations', { method: 'POST', body });
  const next = { ...e, remote: { id: r.id, version: r.version }, updatedAt: Date.now() } as AnyEntry;
  await db.put('creations', next);
  catalog.put(next);
  return r;
}

// ---------- Workshop ----------
export interface WorkshopStatus { enabled: boolean; limit: number; remaining: number }
export const workshopStatus = () => api<WorkshopStatus>('/workshop/status');
export const generate = (body: C.GenerateBody) => api<C.GenerateResponse>('/workshop/generate', { method: 'POST', body });

// ---------- Outfit ----------
/**
 * Wears a cosmetic: an official one (the slot's historical pattern) or a created/bought one (embedded in the outfit).
 * Body and face are set with setBody.
 */
export function wear(e: Entry<'cosmetic'>) {
  const slot = e.creation.content.slot, official = officialCosmeticOf(e.id);
  update(s => {
    const look = { ...s.profile.look, wear: { ...(s.profile.look.wear || {}) } };
    delete look.wear[slot];
    if (official) { if (slot === 'jersey') look.pattern = official.id; else if (slot === 'frame' || slot === 'helmet') look[slot] = official.id; }
    else look.wear[slot] = { id: e.remote?.id ?? e.id, name: e.creation.name, content: e.creation.content };
    s.profile.look = look;
  });
}
/** Cosmetic worn in a slot (catalogue id), for display. */
export function wornId(slot: C.CosmeticSlot): string {
  const look = state.profile.look, w = look.wear?.[slot];
  if (w) return catalog.get(w.id)?.id ?? (catalog.list('cosmetic').find(e => e.remote?.id === w.id)?.id || w.id);
  return `${slot}-${slot === 'jersey' ? look.pattern || 'uni' : slot === 'wheels' ? 'carbone' : look[slot] || 'couleur'}`;
}
export function setBody(patch: Partial<C.Body>) { update(s => { s.profile.look = { ...s.profile.look, body: { ...C.DEFAULT_BODY, ...(s.profile.look.body || {}), ...patch } }; }); }

/** Ambience of a race: theme chosen in settings (official or created), otherwise golden hour. */
export function themeContent(id: string = state.settings.mood): C.ThemeContent {
  return catalog.get(id, 'theme')?.creation.content ?? OFFICIAL_THEMES.golden.content;
}
/**
 * World and look of a track (ADR 0020): from the catalogue, or, for a track met in a room, from its published creation.
 * Network data goes through the schema like any other; a track that cannot be fetched is ridden in the generic world.
 */
export async function trackLook(trackId: string): Promise<{ world: C.WorldContent | null; mood: C.ThemeContent | null }> {
  let content: unknown = catalog.get(trackId, 'track')?.creation.content ?? (OFFICIAL_WORLDS[trackId] ? { world: OFFICIAL_WORLDS[trackId] } : null);
  if (!content && C.CreationId.safeParse(trackId).success && !TRACKS[trackId]) {
    content = await fetchCreation(trackId).then(c => (c.kind === 'track' ? c.content : null)).catch(() => null);
  }
  const parsed = C.TrackLook.safeParse(content ?? {});
  if (!parsed.success) return { world: null, mood: null };
  return { world: worldOf(parsed.data), mood: parsed.data.mood ?? null };
}
/** A track's own mood, unless the player prefers to keep theirs. */
export function moodOf(look: { mood: C.ThemeContent | null }, chosen: string): C.ThemeContent {
  return look.mood && state.settings.trackMood !== false ? look.mood : themeContent(chosen);
}
/** Ambience of races: chosen theme if created or unlocked, otherwise golden hour. */
export function rideThemeId(): string {
  const id = state.settings.mood, theme = catalog.get(id, 'theme');
  return theme && (!theme.level || isUnlocked(levelFromXp(state.progress.xp).level, 'mood', id)) ? id : 'golden';
}
export { ApiError };

// ---------- Bridges to the engine ----------
/** Workout in the engine format, from any catalogue source. */
export function workoutOf(id: string): Workout | null {
  const e = catalog.get(id, 'workout');
  return e ? toWorkout(e.id, e.creation) : null;
}

/** Race configuration: official mode and circuit by id, or embedded creations (rules, track). */
export interface RaceConfig {
  mode: string; track: string; laps?: number; bots?: number; diff?: string; groupWkg?: number; elastic?: boolean;
  rules?: C.ModeContent['rules']; trackDef?: TrackDef; modeName: string; creationId?: string;
}
export function raceConfig(base: { mode: string; track: string; laps?: number; bots?: number; diff?: string; groupWkg?: number; elastic?: boolean; items?: string[]; itemsOff?: string[] }): RaceConfig {
  const m = isModeId(base.mode) ? null : catalog.get(base.mode, 'mode'), t = catalog.get(base.track, 'track');
  // community items added by the player: embedded in the rules (playable offline and in a room)
  const extra = (base.items || []).map(id => catalog.get(id, 'item')).filter((e): e is Entry<'item'> => !!e).map(e => toCustomItem(e.id, e.creation));
  const baseRules = m ? m.creation.content.rules : isModeId(base.mode) ? MODES[base.mode].rules : null;
  const customItems = [...(baseRules?.customItems || []), ...extra].slice(0, 12);
  const full = m || (extra.length && baseRules?.items) ? { ...baseRules!, ...(customItems.length ? { customItems } : {}) } : undefined;
  // unticked items travel in the rules, even for an official mode, which otherwise needs none
  const from = full ?? baseRules, kept = from ? withoutItems(from, base.itemsOff) : null;
  const rules = kept && kept !== from ? kept : full;
  return {
    ...base,
    ...(rules ? { rules } : {}),
    ...(m ? { modeName: m.creation.name, creationId: m.remote?.id ?? m.id } : { modeName: isModeId(base.mode) ? MODES[base.mode].name : 'Race' }),
    ...(t && t.source !== 'builtin' ? { trackDef: toTrackDef(t.id, t.creation), laps: base.laps ?? t.creation.content.laps } : {}),
    // a road from A to B is ridden once (ADR 0029): its ghost and its results are those of one run
    ...(t?.creation.content.open ? { laps: 1 } : {}),
  };
}

/** Current program: catalogue entry, resolved slots (replacements included) and next workout. */
export function currentPlan() {
  const p = state.plan && catalog.get(state.plan.id, 'plan');
  if (!p) return null;
  const C = p.creation.content, ctx = catalog.ctx, adjust = state.plan!.adjust;
  const weeks = C.weeks.map((w, wi) => ({ focus: w.focus, sessions: w.sessions.map((s, i) => { const key = `${wi}-${i}`; return { key, day: s.day, note: s.note, optional: !!s.optional, swapped: !!adjust?.swaps[key], skipped: state.plan!.done[key] === 'skipped', workout: planSlot(C, key, adjust, ctx) }; }) }));
  const next = weeks.flatMap((w, wi) => w.sessions.map(s => ({ ...s, week: wi }))).find(s => !state.plan!.done[s.key] && s.workout) || null;
  return { entry: p, weeks, next, total: planTotal(C), done: Object.keys(state.plan!.done).length, intensity: adjust?.intensity ?? 1, log: adjust?.log ?? [] };
}
export function startPlan(e: Entry<'plan'>) { update(s => { s.plan = { id: e.id, start: Date.now(), done: {}, total: planTotal(e.creation.content) }; }); }
/** Intensity to apply to a program workout (adaptation rules). */
export const planIntensity = (planKey: string | null | undefined) => (planKey && state.plan?.adjust?.intensity) || 1;

/**
 * Applies the program's adaptation rules to the latest activities (fatigue, missed or crushed workouts, lag).
 * Idempotent: an activity is evaluated only once. Returns the new notes to display.
 */
export async function refreshPlan(): Promise<string[]> {
  const p = state.plan && catalog.get(state.plan.id, 'plan');
  if (!p || state.plan!.completedAt || !p.creation.content.adapt.length) return [];
  const acts = await listActivities().catch(() => []);
  const tsb = fitnessSeries(acts, 90, Date.now()).at(-1)?.tsb ?? 0;
  const r = adaptPlan(p.creation.content, state.plan!, {
    activities: acts.map(a => ({ id: a.id, date: a.date, meta: { planKey: a.planKey, compliance: a.compliance, completed: a.completed }, summary: a.summary })),
    tsb, now: Date.now(), findWorkout: catalog.ctx.findWorkout,
  });
  if (JSON.stringify(r.adjust) !== JSON.stringify(state.plan!.adjust ?? null) || JSON.stringify(r.done) !== JSON.stringify(state.plan!.done)) update(s => { if (s.plan) { s.plan.adjust = r.adjust; s.plan.done = r.done; } });
  return r.notes;
}

/** Reports a published creation (hidden after several reports). */
export const report = (id: string, reason: (typeof C.REPORT_REASONS)[number], note?: string) => api(`/creations/${encodeURIComponent(id)}/report`, { method: 'POST', body: { reason, ...(note ? { note } : {}) } });
